import React, { useState } from "react";
import styles from "./RecoverStreakModal.module.css";

interface RecoverStreakModalProps {
  isOpen: boolean;
  onClose: () => void;
  lastStreak: number;
  recoverStreakPayment: string; // BNB amount as string
  onRecover: () => Promise<void>;
}

const RecoverStreakModal: React.FC<RecoverStreakModalProps> = ({
  isOpen,
  onClose,
  lastStreak,
  recoverStreakPayment,
  onRecover,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleRecover = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      await onRecover();
      onClose();
    } catch (error: any) {
      console.error("Recovery failed:", error);
      setError(error.message || "Failed to recover streak. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>Recover Your Streak</h2>
          <button className={styles.closeButton} onClick={onClose} disabled={isProcessing}>
            ×
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.streakInfo}>
            <div className={styles.streakValue}>🔥 {lastStreak}</div>
            <p>Forgot to play yesterday? Nevermind, you have a chance to pay <strong>{recoverStreakPayment} BNB</strong> to recover your streak to {lastStreak}.</p>
          </div>

          <div className={styles.paymentInfo}>
            <h4>How it works:</h4>
            <ol>
              <li>Click "Recover Streak" to initiate a BNB payment</li>
              <li>Confirm the transaction in your wallet</li>
              <li>Wait for blockchain confirmation</li>
              <li>Your streak will be automatically restored!</li>
            </ol>
          </div>

          {error && <div className={styles.error}>{error}</div>}
        </div>

        <div className={styles.actions}>
          <button className={styles.cancelButton} onClick={onClose} disabled={isProcessing}>
            Cancel
          </button>
          <button className={styles.recoverButton} onClick={handleRecover} disabled={isProcessing}>
            {isProcessing ? "Processing..." : `Recover Streak`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecoverStreakModal;
